import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import useAppLayout from './hooks/useAppLayout';
import API_URL from './api/config'; // Make sure this resolves correctly
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { toast, ToastContainer } from 'react-toastify';
import ReactModal from 'react-modal';
import { Link, useNavigate } from 'react-router-dom';
import imageCompression from 'browser-image-compression';



let orgChartScriptLoaded = false;
let orgChartLoadPromise = null;

const loadOrgChartScript = (src) => {
  if (orgChartScriptLoaded) {
    return Promise.resolve();
  }
  if (orgChartLoadPromise) {
    return orgChartLoadPromise;
  }

  orgChartLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => {
      orgChartScriptLoaded = true;
      resolve();
    };
    script.onerror = reject;
    document.body.appendChild(script);
  });

  return orgChartLoadPromise;
};



function FamilyTree() {
  
  const navigate = useNavigate();
  const { menu, toggleMobileMenu } = useAppLayout();

  const loggedInUserId = localStorage.getItem("user_id");
  const loggedFamilyAdmin = localStorage.getItem("family_admin");

  const { isSidebarVisible, showSidebar, toggleSidebar } = useAppLayout();
  const chartContainerRef = useRef(null);
  const chartInstanceRef = useRef(null); // Ref to store the OrgChart instance

  const queryClient = useQueryClient();

  const getToken = () => localStorage.getItem('token');

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState({
    id: '', name: '', alt_name: '', img: '', gender: '',
    married: '', single: '', single_mother: '', spouse: '', unknown: '',
  });

  // New state for chart-specific loading
  const [isChartLoading, setIsChartLoading] = useState(true); // Default to true to show spinner initially
  const [showSpinner, setShowSpinner] = useState(false); // State to control spinner visibility with delay

  const openModalWithData = (data) => {
    setSelectedMember({
      id: data.id || '',
      name: data.name || '',
      alt_name: data.alt_name || '',
      img: data.img || '',
      gender: data.gender || '',
      married: data.married || '',
      single: data.single || '',
      single_mother: data.single_mother || '',
      spouse: data.spouse || '',
      unknown: data.unknown || '',
    });
    setModalIsOpen(true);
  };

  // Query to fetch family tree data
  const { data: familyTreeData, isLoading: isLoadingFamilyTree, isError: isErrorFamilyTree } = useQuery({
    queryKey: ['familyTree'],
    queryFn: () =>
      axios
        .get(`${API_URL}/family-tree`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        })
        .then((res) => res.data),
    staleTime: 0, // Data is immediately stale and will be refetched if re-rendered/accessed
    cacheTime: 10 * 60 * 1000, // Keep data in cache for 10 minutes
  });

  // Handle spinner visibility with a delay to prevent flicker
  useEffect(() => {
    let timer;
    // Show spinner if either data is loading or chart is rendering, and only after 500ms
    if (isLoadingFamilyTree || isChartLoading) {
      timer = setTimeout(() => setShowSpinner(true), 500);
    } else {
      setShowSpinner(false); // Hide spinner immediately when not loading
    }
    return () => clearTimeout(timer); // Clear timer if state changes before 500ms
  }, [isLoadingFamilyTree, isChartLoading]);


  // Load OrgChart script and initialize/update chart when data is available
  useEffect(() => {
    if (!familyTreeData) {
      setIsChartLoading(true); // Keep chart loading state true until data is there
      return;
    }

    // Set chart loading to true while we prepare to render/update it
    setIsChartLoading(true);

    loadOrgChartScript(`${process.env.PUBLIC_URL}/orgchart/orgchart.js`)
      .then(() => {
        if (window.OrgChart) {
          initializeOrgChart(familyTreeData);
        }
      })
      .catch((error) => {
        console.error('Failed to load OrgChart script:', error);
        setIsChartLoading(false); // If script fails, stop loading
      });

    // Cleanup function for OrgChart instance
    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [familyTreeData]); // Runs when familyTreeData updates




  

  const getInitials = (name = '') => {
    // Strip parenthetical suffixes like "(D)", "(dec)", etc.
    const clean = name.replace(/\s*\(.*?\)\s*/g, '').trim();
    const parts = clean.split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return (parts[0]?.[0] ?? '?').toUpperCase();
  };
  
  const makeInitialsAvatar = (name = '', bgColor = '#ED93B1') => {
    const clean = name.replace(/\s*\(.*?\)\s*/g, '').trim();
    const initials = getInitials(clean);
  
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="86" height="86">
        <circle cx="43" cy="43" r="43" fill="${bgColor}"/>
        <text
          x="43" y="43"
          text-anchor="middle"
          dominant-baseline="central"
          font-family="system-ui, sans-serif"
          font-size="28"
          font-weight="600"
          fill="#ffffff"
          letter-spacing="1"
        >${initials}</text>
      </svg>
    `.trim();
  
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  };

  const patchData = (rawData) =>
    rawData.map(node => {
      const isBlue = Array.isArray(node.tags) && node.tags.includes('blue');
      const bgColor = isBlue ? '#85B7EB' : '#ED93B1';
      return {
        ...node,
        img: node.img && node.img.trim() ? node.img : makeInitialsAvatar(node.name ?? '', bgColor),
      };
    });

  

  const initializeOrgChart = (data) => {
    if (!window.OrgChart || !chartContainerRef.current) {
      setIsChartLoading(false); // Ensure loading is off if requirements not met
      return;
    }

    // This part is crucial: ALWAYS set loading to true when you start loading/updating the chart
    // and then rely on the 'ready' event to set it to false.
    setIsChartLoading(true);

    if (chartInstanceRef.current) {
      // If an instance already exists, load new data into it.
      // The 'ready' event listener is already attached or will be re-attached below.

      chartInstanceRef.current.load(patchData(data));

      //chartInstanceRef.current.load(data);
    } else {
      // Create a new instance if one doesn't exist
      const isMobile = window.innerWidth <= 768;
      const OrgChart = window.OrgChart;

      OrgChart.templates.family_template = Object.assign({}, OrgChart.templates.ana);
      OrgChart.templates.family_template.size = [86, 86];
      OrgChart.templates.family_template.plus = '';
      OrgChart.templates.family_template.minus = '';
      OrgChart.templates.family_template.rippleRadius = 40;
      OrgChart.templates.family_template.name = '<text style="font-size: 12px; font-weight: 600;" fill="#000000" x="43" y="100" text-anchor="middle">{val}</text>';
      OrgChart.templates.family_template.title = '<text style="font-size: 12px;" fill="#aeaeae" x="43" y="115" text-anchor="middle">{val}</text>';
   
   
    const baseImg = '<clipPath id="{randId}"><circle cx="43" cy="43" r="40.5"></circle></clipPath><image preserveAspectRatio="xMidYMid slice" clip-path="url(#{randId})" xlink:href="{val}" x="2" y="2" width="82" height="82"></image>';
 
OrgChart.templates.family_template.node = '<circle stroke-width="1" fill="#ED93B1" stroke="#ED93B1" cx="43" cy="43" r="41.5"></circle>';
OrgChart.templates.family_template.img = baseImg + `
  <circle fill="#ffffff" stroke="#cccccc" stroke-width="1" cx="77" cy="72" r="10"></circle>
  <text x="77" y="77" text-anchor="middle" font-size="16" fill="#ED93B1" font-weight="bold">+</text>
`;

      OrgChart.templates.family_template_blue = Object.assign({}, OrgChart.templates.family_template);
      
    OrgChart.templates.family_template_blue.node = '<circle stroke-width="1" fill="#85B7EB" stroke="#85B7EB" cx="43" cy="43" r="41.5"></circle>';
OrgChart.templates.family_template_blue.img = baseImg + `
  <circle fill="#ffffff" stroke="#cccccc" stroke-width="1" cx="77" cy="72" r="10"></circle>
  <text x="77" y="77" text-anchor="middle" font-size="16" fill="#85B7EB" font-weight="bold">+</text>
`;

      const chart = new OrgChart(chartContainerRef.current, {
        template: 'family_template',
        enableSearch: true,
        levelSeparation: isMobile ? 60 : 100,
        siblingSeparation: isMobile ? 60 : 100,
        scaleInitial: isMobile ? OrgChart.match.boundary : 1,
        scaleMin: 0.5,
        scaleMax: 3,
        nodeBinding: {
          name: 'name',
          title: 'title',
          img: 'img',
        },
        tags: {
          blue: { template: 'family_template_blue' },
          partner: { template: 'family_template' },
        },
        nodeMouseClick: OrgChart.action.none, // override default popup
      });

      chart.on('render-link', (sender, args) => {
        
        if (args.cnode.ppid !== undefined) {
          args.html += '<use xlink:href="#baby" x="' + args.p.xa + '" y="' + args.p.ya + '"/>';
        }
      });

      chart.on('redraw', function () {
        const chartElement = document.querySelector('#tree svg');
        if (chartElement) {
          chartElement.style.touchAction = 'pan-x pan-y';
        }
      });

      chart.on('click', (sender, args) => {
        if (args && args.node) {
          const nodeData = sender.get(args.node.id);
          setTimeout(() => {
            openModalWithData(nodeData);
          }, 150); // Slight delay fixes the flash
        }
      });

      

        chart.load(patchData(data));
      //chart.load(data);
      chartInstanceRef.current = chart;
    }

    // Ensure the 'ready' event listener is always attached/re-attached
    // This will set isChartLoading to false once the chart has finished rendering
    if (chartInstanceRef.current) {
        // Remove previous listener to prevent multiple calls if initializeOrgChart runs again
        // This is good practice if you re-attach listeners on every render
        chartInstanceRef.current.off('ready');
        chartInstanceRef.current.on('ready', () => {
            setIsChartLoading(false);
        });
    }
  };


  

  // Existing state and effects for modals, relationships, etc. (unchanged)
  const [addFamilyModalIsOpen, setAddFamilyModalIsOpen] = useState(false);
  const openAddFamilyModal = () => {
    setAddFamilyModalIsOpen(true);
  };
  const closeAddFamilyModal = () => {
    setAddFamilyModalIsOpen(false);
    resetForm(); // Reset form when closing
  };

  const [selectedRelationship, setSelectedRelationship] = useState('');
  const [isDeceased, setIsDeceased] = useState(false);
  const [isInactive, setIsInactive] = useState(false);
  const [isUnderage, setIsUnderage] = useState(false);
  const [selectedPrefix, setSelectedPrefix] = useState("");

  const getPrefixOptions = () => {
    const maleRelations = ['Father', 'Son', 'Brother'];
    const femaleRelations = ['Mother', 'Daughter', 'Sister', 'Spouse'];
    const neutral = ['Dr', 'Prof', 'Rev'];

    if (maleRelations.includes(selectedRelationship)) {
      return ['Mr', ...neutral];
    }
    if (femaleRelations.includes(selectedRelationship)) {
      return ['Mrs', ...neutral];
    }
    return ['Mr', 'Mrs', ...neutral];
  };

  // Spouse list
  const [spouseList, setSpouseList] = useState([]);
  useEffect(() => {
    if (addFamilyModalIsOpen && selectedMember) {
      fetchSpouses();
    }
  }, [addFamilyModalIsOpen, selectedMember]);

  const fetchSpouses = async () => {
    try {
      const response = await axios.post(`${API_URL}/family-tree-get-spouse-api`, {
        withCredentials: true,
        selected_id: selectedMember.id,
      });
      setSpouseList(response.data);
    } catch (error) {
      console.error('Error fetching spouses:', error);
    }
  };

  // Mother list
  const [motherList, setMotherList] = useState([]);
  useEffect(() => {
    if (addFamilyModalIsOpen && selectedMember) {
      fetchMothers();
    }
  }, [addFamilyModalIsOpen, selectedMember]);

  const fetchMothers = async () => {
    try {
      const response = await axios.post(`${API_URL}/family-tree-get-mother-api`, {
        withCredentials: true,
        selected_id: selectedMember.id,
      });
      setMotherList(response.data);
    } catch (error) {
      console.error('Error fetching mothers:', error);
    }
  };

  const [hasFather, setHasFather] = useState(false);
  const [hasMother, setHasMother] = useState(false);
  useEffect(() => {
    if (selectedMember?.id) {
      axios.post(`${API_URL}/family-tree-check-father-mother-api`, {
        selected_id: selectedMember.id
      }, { withCredentials: true })
        .then(res => {
          setHasFather(res.data.hasFather);
          setHasMother(res.data.hasMother);
        })
        .catch(err => {
          console.error("Error fetching parent/spouse info:", err);
        });
    }
  }, [selectedMember]);

  const getRelationshipOptions = () => {
    const { gender, single, single_mother } = selectedMember || {};
    let baseOptions = [];

    if (gender === 'Male' && single_mother === "") {
      baseOptions = ['Father', 'Spouse'];
      if (hasFather) baseOptions.push('Mother');
      if (hasFather && hasMother) baseOptions.push('Step-Mother');
      if (spouseList.length > 0) {
        baseOptions.push('Son', 'Daughter');
      }
    } else if (gender === 'Male' && single_mother === 1) {
      
        baseOptions.push('Son', 'Daughter');
      

    } else if (gender === 'Female' && single === 1) {
      baseOptions = ['Father'];
      if (hasFather) baseOptions.push('Mother');
      if (hasFather && hasMother) baseOptions.push('Step-Mother');
    }

    if (hasFather) {
      baseOptions = baseOptions.filter(rel => rel !== 'Father');
    }
    if (hasMother) {
      baseOptions = baseOptions.filter(rel => rel !== 'Mother');
    }
    if (!(hasFather && hasMother)) {
      baseOptions = baseOptions.filter(rel => rel !== 'Step-Mother');
    }
    if (hasFather && hasMother) {
      baseOptions.push('Brother', 'Sister');
    }
    return baseOptions;
  };

  // Image preview
  const [imageFile, setImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  // Form data for adding member
  const [formData, setFormData] = useState({
    name: '', last_name: '', surname: '', gender: '', date_of_birth: '', date_of_death: '',
    phone_number: '', email: '', relationship: '', prefix: '',
    deceased: false, inactive: false, underage: false,
    selected_id: '', mother_id: ''
  });

  useEffect(() => {
    if (selectedMember) {
      setFormData((prev) => ({
        ...prev,
        selected_id: selectedMember.id,
      }));
    }
  }, [selectedMember]);

  // Helper to reset the form
  const resetForm = () => {
    setFormData({
      name: '', last_name: '', surname: '', gender: '', date_of_birth: '', date_of_death: '',
      phone_number: '', email: '', relationship: '', prefix: '',
      deceased: false, inactive: false, underage: false,
      selected_id: selectedMember?.id || '',
      mother_id: ''
    });
    setImageFile(null);
    setPreviewImage(null); // Reset preview image
    setSelectedRelationship('');
    setSelectedPrefix('');
    setIsDeceased(false);
    setIsInactive(false);
    setIsUnderage(false);
  };

  // useMutation for adding a family member
  const addMemberMutation = useMutation({
    mutationFn: async (submissionData) => {
      const response = await axios.post(
        `${API_URL}/family-tree-add-member-api`,
        submissionData,
        { withCredentials: true }
      );
      return response.data;
    },
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ['familyTree'] }); // Invalidate query on success
        closeAddFamilyModal();
        setModalIsOpen(false); // Close parent modal if open
        toast.success("Family member added successfully", { position: "top-center", autoClose: 3000 });
        resetForm();
      } else {
        toast.error(data.message || "Failed to add member", { position: "top-center", autoClose: 3000 });
      }
    },
    onError: (error) => {
      console.error('Error submitting form:', error);
      const backendMsg = error.response?.data?.message || error.message;
      toast.error(backendMsg || "An error occurred while adding member.", {
        position: "top-center",
        autoClose: 3000,
      });
      setIsChartLoading(false); // Important: if mutation fails, ensure spinner stops
    },
    onSettled: () => {
      
    }
  });


  const [phoneMessage, setPhoneMessage] = useState("");

  const handlePhoneChange = (e) => {
  let value = e.target.value;

  // Detect spaces
  if (/\s/.test(value)) {
    setPhoneMessage("⚠️ Spaces are not allowed. They were removed automatically.");
  } else {
    setPhoneMessage("");
  }

  // Remove spaces
  value = value.replace(/\s+/g, "");

  // Allow only numbers and +
  if (!/^[0-9+]*$/.test(value)) return;

  // Auto-fix: 7XXXXXXXX → 07XXXXXXXX
  if (/^7\d{8}$/.test(value)) {
    value = "0" + value;
    setPhoneMessage("ℹ️ Added leading 0 automatically.");
  }

  // Prevent wrong first character
  if (value.length === 1 && value !== "0" && value !== "+") return;

   // Limit: 15 digits max (E.164)
   const digitsCount = value.replace(/\D/g, "").length;

   if (digitsCount > 15) {
     //setPhoneMessage("❌ Phone number cannot exceed 15 digits.");
     return;
   }


  setFormData({
    ...formData,
    phone_number: value,
  });
};



const [isSubmitting, setIsSubmitting] = useState(false);

const handleSubmitMemberDetails = async () => {
  if (isSubmitting) return; // prevent multiple clicks
  setIsSubmitting(true);

  try {
    if (!formData.relationship) {
      toast.error("Relationship is required", { position: "top-center", autoClose: 3000 });
      return;
    }

    const needsMotherId = ['Brother', 'Sister', 'Son', 'Daughter'].includes(formData.relationship);

    if (!formData.name) {
      toast.error("First name is required", { position: "top-center", autoClose: 3000 });
      return;
    }

    const isExemptFromContact = formData.deceased || formData.inactive || formData.underage;
    const hasPhoneOrEmail = formData.phone_number?.trim() || formData.email?.trim();

    if (!isExemptFromContact && !hasPhoneOrEmail) {
      toast.error("Please provide at least a phone number or an email", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    const submissionData = new FormData();

    if (needsMotherId) {
      if (!formData.mother_id) {
        toast.error("Mother is required for this relationship", { position: "top-center", autoClose: 3000 });
        return;
      }
      submissionData.append('mother_id', formData.mother_id);
    }

    for (const key in formData) {
      if (!['deceased', 'inactive', 'underage', 'mother_id'].includes(key)) {
        if (formData[key] !== undefined && formData[key] !== null) {
          submissionData.append(key, formData[key]);
        }
      }
    }
    submissionData.append('deceased', formData.deceased ? '1' : '0');
    submissionData.append('inactive', formData.inactive ? '1' : '0');
    submissionData.append('underage', formData.underage ? '1' : '0');

    if (imageFile) {
      try {
        const targetKB = 100;
        let quality = 0.9;
        let compressedFile = imageFile;

        while (true) {
          const options = { maxSizeMB: targetKB / 1024, initialQuality: quality, useWebWorker: true };
          compressedFile = await imageCompression(imageFile, options);
          if (compressedFile.size / 1024 <= targetKB || quality <= 0.1) break;
          quality -= 0.1;
        }
        submissionData.append('image', compressedFile);
      } catch (err) {
        console.error('Image compression error:', err);
        toast.warn("Image compression failed. Uploading original image.", { position: "top-center" });
        submissionData.append('image', imageFile);
      }
    }

    if (selectedMember?.id) {
      submissionData.append('selected_member_id', selectedMember.id);
    }

    // Trigger the mutation
    addMemberMutation.mutate(submissionData, {
      onSettled: () => setIsSubmitting(false), // reset submission state after success/error
    });
    
  } catch (error) {
    console.error(error);
    toast.error("An error occurred while submitting", { position: "top-center" });
    setIsSubmitting(false);
  }
};



  const { data: treeExpired, isLoadingExpiry } = useQuery({
    queryKey: ["userExpiryDetails"],
    queryFn: () =>
      axios.get(`${API_URL}/user-payment-details-api`, { withCredentials: true })
        .then((res) => res.data),
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
  
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  
  useEffect(() => {
    if (!isLoadingExpiry && treeExpired) {
      if (treeExpired.expired) {
        setShowSubscribeModal(true);
      } else {
        setShowSubscribeModal(false);
      }
    }
  }, [treeExpired, isLoadingExpiry]);
  

   const [loading, setLoading] = useState(false);
    
        const handleCheckPackages = async () => {
          setLoading(true);
        
          try {
            const response = await axios.post(
              `${API_URL}/checking-the-packages-subscription-api`,
              {},
              { withCredentials: true }
            );
        
            if (response.data.success) {
              const verifyCode = response.data.verify_code;
              navigate(`/tree-subscription-web?verifyCode=${verifyCode}`);
              
            } else {
              toast.error(response.data.message || "Something went wrong");
            }
          } catch (error) {
            toast.error("Error sending details");
            console.error(error);
          } finally {
            setLoading(false);
          }
        };
  
   

    const handleSendNotification = async () => {
      setLoading(true);
    
      try {
        const response = await axios.post(
          `${API_URL}/sending-notification-subscription-api`,
          {}, // no body needed
          { withCredentials: true } //config goes here
        );
    
        toast.success(response.data.message || "Notification sent successfully, kindly wait" , { position: "top-center", autoClose: 6000 });
      } catch (error) {
        toast.error("Error sending details");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
   


    const [treeBgColor, setTreeBgColor] = useState("#EBF4ED");

    const fetchTreeBackground = async () => {
      try {
        const res = await axios.get(
          `${API_URL}/get-family-tree-background-api`,
          { withCredentials: true }
        );
    
        if (res.data?.color_code) {
          setTreeBgColor(res.data.color_code);
        }
      } catch (error) {
        console.error("Failed to load tree background color", error);
      }
    };

    useEffect(() => {
      fetchTreeBackground();
    }, []);



    
  if (isErrorFamilyTree) {
    return <p style={{ textAlign: 'center', color: 'red' }}>Error loading family tree data.</p>;
  }

  return (
    <div className={`main-wrapper ${menu ? 'slide-nav': ''}`}>
    <Header onMenuClick={(value) => toggleMobileMenu()} />
    <Sidebar />
        <ToastContainer />

        <div className="page-wrapper">
        <div className="content container-fluid">

            {/*<ReactModal
              isOpen={showSubscribeModal}
              onRequestClose={() => setShowSubscribeModal(false)} // still needed for manual close
              shouldCloseOnOverlayClick={false}
              shouldCloseOnEsc={false}
              parentSelector={() => document.querySelector('.page-wrapper')}
              className="Modal"
              overlayClassName="Overlay"
              style={{
                overlay: {
                  
                  backgroundColor: "rgba(0,0,0,0.5)",
                },
                content: {
                  maxWidth: "400px",
                  margin: "auto",
                  textAlign: "center",
                  padding: "20px",
                },
              }}
            >
              <h5>Kindly subscribe</h5>
              <p>Your subscription expired. Please subscribe to continue.</p>
            
            <button className="btn btn-primary" onClick={handleSendNotification}
            disabled={loading}>
              {loading ? 'Sending...' : 'Get the notification for subscription'}
            </button>
            
            <Link to='/edit-personal-info-subscription' className='btn btn-outline-primary mt-4'>Edit phone number / email for notification?</Link>
            
            </ReactModal>*/}
            
                        
                       {/*<div className="d-flex justify-content-end mb-3 gap-4" style={{
                                position: "absolute",
                                zIndex: 100,
                               marginRight: '5%',
                                right: 0,
                              }}>
                                <Link to='/print-tree-order' style={{color: '#184A2C', fontWeight: '900', fontSize: '20px', textDecoration: 'none'}}>
                                <i className="fa fa-shopping-cart" style={{ fontSize: "20px", color: "#184A2C" }}></i> Order </Link>
                        </div>*/}
                        
                        {/* OrgChart Container - its visibility/opacity is controlled by the spinner */}
                        <div
                          id="tree"
                          ref={chartContainerRef}
                          style={{
                            width: '100%',
                            height: '650px',
                            backgroundColor: treeBgColor,
                            opacity: showSpinner ? 0 : 1, // Chart is hidden when spinner is shown
                            transition: 'opacity 0.5s ease-in-out' // Smooth transition for showing/hiding chart
                          }}>
                        </div>
            
                        {/* Loading overlay with dimmed background */}
                        {showSpinner && (
                            
                          <div className="loadingOverlay" style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            flexDirection: 'column', // Changed to column to stack spinner and text
                            justifyContent: 'center',
                            alignItems: 'center',
                            backgroundColor: 'rgba(255, 255, 255, 0.8)', // Semi-transparent background
                            zIndex: 1000 // Ensure it's on top
                          }}>
                            <div className='loadingspinner'></div>
                            <p style={{ textAlign: 'center', color: '#000', marginTop: '10px' }}>Loading tree...</p> {/* Changed text color for better contrast */}
                          </div>
                        )}
            
            
                        {/* Modals for viewing/adding family members */}
                      
            
            
                         <ReactModal 
                                      isOpen={modalIsOpen} 
                                      onRequestClose={() => setModalIsOpen(false)} 
                                      className="modern-modal profile-modal" 
                                      overlayClassName="modern-overlay"
                                      ariaHideApp={false}
                                    >
                                      <div className="modal-container">
                                       
                                        <button 
                                          type="button" 
                                          className="modal-close-btn" 
                                          onClick={() => setModalIsOpen(false)}
                                        >
                                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                          </svg>
                                        </button>
                        
                                        <div className="profile-image-section">
                                          <div className="large-profile-image-wrapper">
                                            {selectedMember.img ? (
                                              <img
                                                src={selectedMember.img}
                                                alt={selectedMember.name}
                                                className="large-profile-image"
                                              />
                                            ) : (
                                              <div className="large-profile-image-placeholder">
                                                <svg width="120" height="120" viewBox="0 0 24 24" fill="currentColor">
                                                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                                                </svg>
                                              </div>
                                            )}
                                            
                                          
                                            <div className="image-overlay-info">
                                              <h2 className="overlay-name">{selectedMember.name}</h2>
                                            </div>
                                          </div>
                                        </div>
                        
                                        <div className="compact-action-buttons">
            
                        { selectedMember.spouse === "" && selectedMember.single === 1 && (
            
                              <button
                              className="btn btn-compact btn-add"
                              onClick={openAddFamilyModal}
                              >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M12 5v14m-7-7h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              Add Family
                              </button>
                              
                              )}
            
            
                            {(selectedMember.spouse === "" && selectedMember.single === "") ? (
                               <button
                               className="btn btn-compact btn-add"
                               onClick={openAddFamilyModal}
                             >
                               <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                 <path d="M12 5v14m-7-7h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                               </svg>
                               Add Family
                             </button>
                              ) : selectedMember.single === "" ? (
                                <button  className="btn btn-compact btn-add"
                                    onClick={() => navigate('/family-tree-spouse', { state: { memberId: selectedMember.id } })}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                      <path d="M12 5v14m-7-7h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Extended Family
                                  </button>
                              ) : (
                                <button
                                  className="btn btn-compact btn-add"
                                  onClick={() => navigate('/family-tree-sister', { state: { memberId: selectedMember.id } })}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 5v14m-7-7h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                  Extended Family
                                </button>
                              )}
            
            
                                  <button 
                                    className="btn btn-compact btn-edit"
                                    onClick={() => navigate('/edit-personal-information', { state: { memberId: selectedMember.id } })}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Edit
                                  </button>
                
                                  <button 
                                    className="btn btn-compact btn-profile"
                                    onClick={() => navigate('/profile', { state: { memberId: selectedMember.id } })}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
                                    </svg>
                                    Profile
                                  </button>
                                </div>
                              </div>
                            </ReactModal>
                        
            
                                  
                              <ReactModal 
                                isOpen={addFamilyModalIsOpen}
                                onRequestClose={closeAddFamilyModal}
                                className="Modal2 add-family-modal"
                                overlayClassName="modern-overlay"
                                ariaHideApp={false}
                              >
                                <div className="modal-container">
                                  
                                <div className="text-center my-3 position-relative">
                                                <button
                                                  type="button"
                                                  className="btn-close position-absolute end-0 me-3"
                                                  aria-label="Close"
                                                  onClick={closeAddFamilyModal}
                                                />
            
                                                {/* Optional icon circle */}
                                                {/* 
                                                <div
                                                  className="rounded-circle bg-success bg-opacity-25 d-inline-flex align-items-center justify-content-center mb-3"
                                                  style={{ width: "64px", height: "64px" }}
                                                >
                                                  <i className="bi bi-person-plus text-success fs-3"></i>
                                                </div>
                                                */}
            
                                                <h5 className="fw-bold mb-1">Add Family Member</h5>
                                                <p className="text-muted mb-0">
                                                  Adding to <span className="fw-semibold text-success">{selectedMember?.name || ""}</span> tree
                                                </p>
                                              </div>
                              
                              
                                  
                                  <div className="modal-body-modern px-3 pt-2 pb-3" style={{marginTop: '-4%'}}>
            
                                  <ToastContainer />
                                    
                                    
                                    <div className="image-upload-section mb-2">
                                      <div
                                        className="image-upload-wrapper"
                                        onClick={() => document.getElementById('uploadImageInput').click()}
                                      >
                                        <div className="image-preview">
                                          <img
                                            src={previewImage || 'img/avatar_male.png'}
                                            alt="Preview"
                                            className="preview-img"
                                          />
                                          <div className="upload-overlay">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                              <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                              <circle cx="12" cy="13" r="4" stroke="currentColor" strokeWidth="2"/>
                                            </svg>
                                          </div>
                                        </div>
                                        <input
                                          id="uploadImageInput"
                                          type="file"
                                          accept="image/*"
                                          style={{ display: 'none' }}
                                          onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                              setImageFile(file);
                                              const reader = new FileReader();
                                              reader.onloadend = () => setPreviewImage(reader.result);
                                              reader.readAsDataURL(file);
                                            }
                                          }}
                                        />
                                      </div>
                                    </div>
                              
                                    
                                      <div className='row'>
                                          <div className='col-6 col-md-6'> 
                                        <label className="form-label-modern small mb-1">Select Relationship</label>
                                        <select
                                          className="form-input-modern form-control form-control-sm"
                                          value={formData.relationship}
                                          onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                                        >
                                          <option value="">Choose relationship</option>
                                          {getRelationshipOptions().map((relation) => (
                                            <option key={relation} value={relation}>{relation}</option>
                                          ))}
                                        </select>
                                       
                                          </div>
            
                                          <div className='col-6 col-md-6'>
                                          {(formData.relationship === 'Brother' || formData.relationship === 'Sister') && (
                                        <>
                                          <label className="form-label-modern small mb-1">Select Mother</label>
                                          <select
                                            className="form-input-modern form-control form-control-sm"
                                            value={formData.mother_id || ''}
                                            onChange={(e) => setFormData({ ...formData, mother_id: e.target.value })}
                                          >
                                            <option value="">Choose mother</option>
                                            {motherList.map((mother) => (
                                              <option key={mother.id} value={mother.id}>{mother.name}</option>
                                            ))}
                                          </select>
                                          </>
                                      )}
            
                                        {(formData.relationship === 'Son' || formData.relationship === 'Daughter' && selectedMember.gender === 'Male') && (
                                        <>
                                          <label className="form-label-modern small mb-1">Select Mother</label>
                                          <select
                                            className="form-input-modern form-control form-control-sm"
                                            value={formData.mother_id || ''}
                                            onChange={(e) => setFormData({ ...formData, mother_id: e.target.value })}
                                          >
                                            <option value="">Choose mother</option>
                                            {spouseList.map((spouse) => (
                                              <option key={spouse.id} value={spouse.id}>{spouse.name}</option>
                                            ))}
                                          </select>
                                          </>
                                      )}
                              
            
                                          </div>
            
                                      </div>
                                      
                                     
                                      <div className='row'>
                                        <div className='col-6 col-md-6'>
                                        <label className="form-label-modern small mb-1">Prefix</label>
                                        <select
                                          className="form-input-modern form-control form-control-sm"
                                          value={formData.prefix}
                                          onChange={(e) => setFormData({ ...formData, prefix: e.target.value })}
                                        >
                                          <option value="">Select prefix</option>
                                          {getPrefixOptions().map((prefix) => (
                                            <option key={prefix} value={prefix}>{prefix}</option>
                                          ))}
                                        </select>
                                        </div>
            
                                        <div className='col-6 col-md-6'>
                                        <label className="form-label-modern small mb-1">First Name *</label>
                                        <input
                                          type="text"
                                          className="form-input-modern form-control form-control-sm"
                                          value={formData.name}
                                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                          placeholder="Enter first name"
                                        />
                                        </div>
            
                                        </div>
                              
                                      
                                        <div className='row'>
                                           <div className='col-6 col-md-6'>
                                        <label className="form-label-modern small mb-1">Last Name</label>
                                        <input
                                          type="text"
                                          className="form-input-modern form-control form-control-sm"
                                          value={formData.last_name}
                                          onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                                          placeholder="Enter last name"
                                        />
            
                                          </div>
            
                                          <div className='col-6 col-md-6'> 
                                        <label className="form-label-modern small mb-1">Surname</label>
                                        <input
                                          type="text"
                                          className="form-input-modern form-control form-control-sm"
                                          value={formData.surname}
                                          onChange={(e) => setFormData({ ...formData, surname: e.target.value })}
                                          placeholder="Enter surname"
                                        /> 
                                          </div>
            
                                          </div>
            
            
                                          <div className="form-group full-width mb-2">
                                <label className="form-label-modern small mb-1">Status</label>
                                <div className="checkbox-group">
                                  <label className="checkbox-wrapper">
                                    <input
                                      type="checkbox"
                                      className="checkbox-input"
                                      checked={formData.deceased}
                                      onChange={() =>
                                        setFormData((p) => ({
                                          ...p,
                                          deceased: !p.deceased,
                                          inactive: false,
                                          underage: false,
                                        }))
                                      }
                                    />
                                    <span className="checkbox-label">Deceased</span>
                                  </label>
                              
                                  <label className="checkbox-wrapper">
                                    <input
                                      type="checkbox"
                                      className="checkbox-input"
                                      checked={formData.inactive}
                                      onChange={() =>
                                        setFormData((p) => ({
                                          ...p,
                                          inactive: !p.inactive,
                                          deceased: false,
                                          underage: false,
                                        }))
                                      }
                                    />
                                    <span className="checkbox-label">Inactive</span>
                                  </label>
                              
                                  <label className="checkbox-wrapper">
                                    <input
                                      type="checkbox"
                                      className="checkbox-input"
                                      checked={formData.underage}
                                      onChange={() =>
                                        setFormData((p) => ({
                                          ...p,
                                          underage: !p.underage,
                                          deceased: false,
                                          inactive: false,
                                        }))
                                      }
                                    />
                                    <span className="checkbox-label">Under-age</span>
                                  </label>
                                </div>
                              </div>
            
            
                                          <div className='row'>
                                           <div className='col-6 col-md-6'>
                                      <div className="form-group mb-2">
                                        <label className="form-label-modern small mb-1">Date of Birth</label>
                                        <input
                                          type="date"
                                          className="form-input-modern form-control form-control-sm"
                                          value={formData.date_of_birth}
                                          onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                                        />
                                      </div>
                                          </div>
            
                                          <div className='col-6 col-md-6'>
            
                                          {formData.deceased && (
                                        <div className="form-group mb-2">
                                          <label className="form-label-modern small mb-1">Date of Death</label>
                                          <input
                                            type="date"
                                            className="form-input-modern form-control form-control-sm"
                                            value={formData.date_of_death}
                                            onChange={(e) => setFormData({ ...formData, date_of_death: e.target.value })}
                                          />
                                        </div>
                                      )}
            
                                          </div>
            
                                          </div>
            
            
                                           {!(formData.deceased || formData.inactive || formData.underage) && (
                                        <>
                                          <div className='row'>
                                          <div className='col-6 col-md-6'>
            
                                          <div className="form-group mb-2">
                                            <label className="form-label-modern small mb-1">Phone Number</label>
                                            <input
                                              type="text"
                                              className="form-input-modern form-control form-control-sm"
                                              value={formData.phone_number}
                                              //onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                                              onChange={handlePhoneChange}
                                              placeholder="Enter phone number"
                                            />
            
            {phoneMessage && (
              <small className="text-warning d-block mt-1">
                {phoneMessage}
              </small>
            )}
            
                                          </div>
                              
                                          
            
                                          </div>
            
                                          <div className='col-6 col-md-6'>
            
                                          <div className="form-group mb-2">
                                            <label className="form-label-modern small mb-1">Email</label>
                                            <input
                                              type="email"
                                              className="form-input-modern form-control form-control-sm"
                                              value={formData.email}
                                              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                              placeholder="Enter email address"
                                            />
                                          </div>
            
                                          </div>
            
                                          </div>
            
                                        </>
                                      )}
            
            
                                  </div>
                              
                                  
                                  
                                  <div className="modal-footer-modern py-2 px-3 d-flex justify-content-between">
                                    
                                    <button 
                                      onClick={closeAddFamilyModal}
                                      className="btn btn-cancel-modern btn-sm"
                                    >
                                      Cancel
                                    </button>
                                    <button 
                                      onClick={handleSubmitMemberDetails} 
                                      className="btn btn-primary-modern btn-sm"
                                      disabled={isSubmitting}
                                    >
                                      {isSubmitting ? (
                                        <>
                                          <svg className="loading-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <circle cx="12" cy="12" r="3" fill="currentColor">
                                              <animate attributeName="r" values="3;5;3" dur="1s" repeatCount="indefinite"/>
                                            </circle>
                                          </svg>
                                          Saving...
                                        </>
                                      ) : (
                                        <>
                                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                            <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <polyline points="17,21 17,13 7,13 7,21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <polyline points="7,3 7,8 15,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                          </svg>
                                          Save Member
                                        </>
                                      )}
                                    </button>
            
                                  </div>
                                </div>
                              </ReactModal>


      </div>
      </div>


      
      <style jsx>{`
      
      :root{
        --brand: #75B437;
        --brand-600: #5fa12f;
        --brand-700: #4d8e29;
        --brand-dark: #184A2C;
        --ink: #000000;
        --bg: #EBF4ED;
        --bg-soft: #f4faf6;
      }

     
      .theme-light, .scrollPageContainer { background: var(--bg); }

  
      .modern-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.35);
        backdrop-filter: blur(8px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
        padding: 20px;
        opacity: 0;
        animation: fadeIn 0.2s ease-out forwards;
        box-sizing: border-box;
        overflow-y: auto;
        min-height: 100vh;
      }
      @keyframes fadeIn { to { opacity: 1; } }
 .modern-modal { 
        background: transparent; 
        border: 0; 
        border-radius: 0; 
        box-shadow: none; 
        outline: none; 
        position: relative; 
        width: auto;
        max-width: none; 
        max-height: none; 
        overflow: visible; 
        margin: auto;
      }

      .modal-container{
        background: linear-gradient(145deg, #ffffff 0%, var(--bg-soft) 100%);
        border-radius: 24px;
        box-shadow: 0 25px 50px -12px rgba(0,0,0,.25), 0 0 0 1px rgba(255,255,255,.8);
        position: relative; 
        overflow: hidden;
        transform: scale(.95) translateY(20px);
        animation: modalSlideIn .3s ease-out forwards;
        backdrop-filter: blur(16px);
        max-height: calc(100vh - 40px);
        overflow-y: auto;
        margin: auto;
      }
      @keyframes modalSlideIn { to { transform: scale(1) translateY(0); } }

      .profile-modal .modal-container { width: 400px; max-width: 90vw; height: 600px; max-height: 85vh; display: flex; flex-direction: column; }
      .profile-image-section { flex: 1; position: relative; overflow: hidden; border-radius: 24px 24px 0 0; }
      .large-profile-image-wrapper{ width:100%; height:100%; position:relative; background: linear-gradient(135deg, var(--brand) 0%, var(--brand-dark) 100%); }
      .large-profile-image{ width:100%; height:100%; object-fit:cover; display:block; }
      .large-profile-image-placeholder{ width:100%; height:100%; display:flex; align-items:center; justify-content:center; color: rgba(255,255,255,.9); background: linear-gradient(135deg, var(--brand) 0%, var(--brand-dark) 100%); }
      .image-overlay-info{ position:absolute; left:0; right:0; bottom:0; background: linear-gradient(transparent, rgba(0,0,0,.75)); padding:40px 20px 20px; text-align:center; }
      .overlay-name{ font-size:28px; font-weight:700; margin:0; color:#fff; text-shadow:0 2px 8px rgba(0,0,0,.5); }





      .add-family-modal .form-input-modern {
  height: 36px !important;      
  padding: 0.25rem 0.5rem !important;
  font-size: 0.85rem !important; 
}
.add-family-modal .form-label-modern {
  margin-bottom: 0.25rem !important; 
  font-size: 0.8rem;
}


      .compact-action-buttons{
        padding:16px 20px; display:flex; gap:8px;
        background: rgba(248, 250, 252, .95);
        backdrop-filter: blur(10px);
        border-radius: 0 0 24px 24px;
        border-top:1px solid rgba(226,232,240,.8);
      }
      .btn-compact{ flex:1; display:flex; align-items:center; justify-content:center; gap:6px; padding:10px 8px; border-radius:10px; font-weight:600; font-size:12px; border:none; cursor:pointer; transition:.2s; outline:none; min-height:44px; }

      .btn-add{
        background: linear-gradient(135deg, var(--brand-dark) 0%, #0f3320 100%);
        color:#fff; box-shadow: 0 2px 8px rgba(24,74,44,.35);
      }
      .btn-add:hover{ transform: translateY(-1px); box-shadow: 0 4px 12px rgba(24,74,44,.45); }

      .btn-edit{
        background: transparent; color: var(--brand-dark);
        border: 2px solid var(--brand);
      }
      .btn-edit:hover{ background: var(--brand); color:#fff; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(117,180,55,.35); }

      .btn-profile{
        background: linear-gradient(135deg, var(--brand) 0%, var(--brand-dark) 100%);
        color:#fff; box-shadow: 0 2px 8px rgba(117,180,55,.35);
      }
      .btn-profile:hover{ transform: translateY(-1px); box-shadow: 0 4px 12px rgba(24,74,44,.45); }

   
      .modal-close-btn{
        position:absolute; top:16px; right:16px; width:36px; height:36px;
        background: rgba(255,255,255,.95); border:0; border-radius:50%;
        display:flex; align-items:center; justify-content:center; cursor:pointer;
        color:#475569; transition:.2s; z-index:10; backdrop-filter: blur(10px);
      }
      .modal-close-btn:hover{ background:#fff; color:#ef4444; transform: scale(1.1); }

  
      .add-fa3mily-modal .modal-container {
  width: 420px;       
  max-width: 92vw;   
  max-height: 72vh;    
}

      .modal-header-modern{ padding:24px 30px; border-bottom:1px solid #e2e8f0; display:flex; align-items:center; justify-content:space-between; background: linear-gradient(135deg, #f5faf7 0%, #e6f0ea 100%); }
      .modal-title-modern{ font-size:20px; font-weight:600; color:#1e293b; margin:0; }
      .highlight-name{ color: var(--brand-dark); font-weight:700; }
      .modal-body-modern{ padding:30px; overflow-y:auto; flex:1; }

      
      .image-upload-section{ display:flex; justify-content:center; margin-bottom:32px; }
      .image-upload-wrapper{ cursor:pointer; transition: transform .2s; }
      .image-upload-wrapper:hover{ transform: scale(1.05); }
      .image-preview{ position:relative; width:110px; height:110px; }
      .preview-img{ width:100%; height:100%; object-fit:cover; border-radius:50%; border:4px solid #e2e8f0; transition: border-color .3s; }
      .image-upload-wrapper:hover .preview-img{ border-color: var(--brand); }
      .upload-overlay{
        position:absolute; bottom:8px; right:8px; width:32px; height:32px;
        background: var(--brand-dark); border-radius:50%;
        display:flex; align-items:center; justify-content:center; color:#fff;
        box-shadow: 0 2px 8px rgba(24,74,44,.35); transition: transform .2s;
      }
      .image-upload-wrapper:hover .upload-overlay{ transform: scale(1.1); }


      
      .form-group{ display:flex; flex-direction:column; }
      .form-group.full-width{ grid-column: 1 / -1; }
      .form-label-modern{ font-size:14px; font-weight:600; color:#374151; margin-bottom:8px; display:flex; align-items:center; gap:4px; }
      .form-input-modern{
        padding:12px 16px; border:2px solid #e2e8f0; border-radius:12px;
        font-size:14px; color:#1e293b; background:#fff; transition:.2s; outline:none;
      }
      .form-input-modern:focus{ border-color: var(--brand); box-shadow: 0 0 0 3px rgba(117,180,55,.2); }
      .form-input-modern::placeholder{ color:#9ca3af; }

      .checkbox-group{ display:flex; flex-wrap:wrap; gap:16px; margin-top:8px; }
      .checkbox-wrapper{ display:flex; align-items:center; cursor:pointer; user-select:none; }
      .checkbox-input{ width:18px; height:18px; margin:0 8px 0 0; cursor:pointer; accent-color: var(--brand-dark); }
      .checkbox-label{ font-size:14px; color:#374151; font-weight:500; }

      .modal-footer-modern{ padding:24px 30px; border-top:1px solid #e2e8f0; display:flex; gap:12px; justify-content:flex-end; background:#f8fafc; }
  .btn{ display:inline-flex; align-items:center; gap:8px; padding:12px 20px; border-radius:12px; font-weight:600; font-size:14px; border:none; cursor:pointer; transition:.2s; outline:none; position:relative; overflow:hidden; }
      .btn:disabled{ opacity:.6; cursor:not-allowed; }
      .btn-primary-modern{
        background: linear-gradient(135deg, var(--brand) 0%, var(--brand-dark) 100%);
        color:#fff; box-shadow: 0 4px 12px rgba(117,180,55,.35);
      }
      .btn-primary-modern:hover:not(:disabled){ transform: translateY(-2px); box-shadow: 0 8px 20px rgba(24,74,44,.45); }
      .btn-cancel-modern{ background:#f1f5f9; color:#64748b; border:2px solid #e2e8f0; }
      .btn-cancel-modern:hover{ background:#e2e8f0; border-color:#cbd5e1; }

      .loading-spinner{ animation: spin 1s linear infinite; }
      @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }


      @media (max-width: 640px){
        .form-grid{ grid-template-columns:1fr; }
        .modal-container{ margin:10px; width: calc(100vw - 20px); }
        .profile-modal .modal-container{ height:500px; }
        .compact-action-buttons{ padding:12px 16px; flex-direction:column; gap:8px; }
        .btn-compact{ width:100%; font-size:13px; padding:12px; }
        .modal-header-modern, .modal-body-modern, .modal-footer-modern{ padding:20px; }
        .overlay-name{ font-size:24px; }
        .checkbox-group{ flex-direction:column; gap:12px; }
        
        .modern-overlay {
          padding: 10px;
          align-items: flex-start;
          padding-top: 20px;
        }
        
        .modal-container { 
          margin: 0;
          width: 100%;
          max-height: calc(100vh - 40px);
        }
      }

      @media (max-width: 480px){
        .compact-action-buttons{ padding:10px 12px; }
        .btn-compact{ font-size:11px; padding:10px 8px; gap:4px; }
        .overlay-name{ font-size:20px; }
        .profile-modal .modal-container{ height:450px; }
      }

    
      :root { --footerbar-height: 64px; }
      .tree-wrapper{ position:relative; width:100%; height:600px; }

.add-family-modal .checkbox-group{
  display: flex !important;
  flex-direction: row !important;
  align-items: center;
  gap: 24px;
  flex-wrap: nowrap;
  white-space: nowrap;
}
.add-family-modal .checkbox-wrapper{
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.add-family-modal .checkbox-input{
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: var(--brand-dark);
}



    `}</style>



      
    </div>
    
  );
}

export default FamilyTree;
